import { useMemo } from 'react';
import { useWillStore } from '../store';

// Address type based on common structure in the codebase
interface Address {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  postcode?: string;
}

export interface SavedPerson {
  fullName: string;
  relationship: string;
  address?: Address;
}

export interface UseSavedPeopleOptions {
  /** Include spouse/partner */
  includeSpouse?: boolean;
  /** Include engaged partner (fiancé/fiancée) */
  includeEngagedPartner?: boolean;
  /** Include children */
  includeChildren?: boolean;
  /** Only include children over 18 (for executor selection) */
  adultsOnlyChildren?: boolean;
  /** Include guardians */
  includeGuardians?: boolean;
  /** Include pet guardians */
  includePetGuardians?: boolean;
  /** Include executors */
  includeExecutors?: boolean;
  /** Include substitute executors */
  includeSubstituteExecutors?: boolean;
  /** Include beneficiaries (excluding charities) */
  includeBeneficiaries?: boolean;
  /** Include manually added people (e.g. via Quick Add) */
  includeManuallyAdded?: boolean;
}

// Helper function to check if someone is over 18 based on their date of birth
const isOver18 = (dateOfBirth: string | undefined): boolean => {
  if (!dateOfBirth) return false;
  const dob = new Date(dateOfBirth);
  const today = new Date();
  const age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    return age - 1 >= 18;
  }
  return age >= 18;
};

/** 
 * Preset configurations for common use cases 
 */
export const SAVED_PEOPLE_PRESETS = {
  /** For executor selection - excludes children under 18 */
  executors: {
    includeSpouse: true,
    includeEngagedPartner: true,
    includeChildren: true,
    adultsOnlyChildren: true,
    includeGuardians: true,
    includePetGuardians: true,
    includeManuallyAdded: true,
  },
  /** For beneficiary selection - includes all family members */
  beneficiaries: {
    includeSpouse: true,
    includeEngagedPartner: true,
    includeChildren: true,
    includeGuardians: true,
    includePetGuardians: true,
  },
  /** For guardian selection - includes spouse and executors */
  guardians: {
    includeSpouse: true,
    includeEngagedPartner: true,
    includeExecutors: true,
    includeManuallyAdded: true,
  },
  /** For gifts - includes family and legal roles */
  gifts: {
    includeSpouse: true,
    includeChildren: true,
    includeGuardians: true,
    includeExecutors: true,
  },
  /** For optionals - includes everyone */
  optionals: {
    includeSpouse: true,
    includeEngagedPartner: true,
    includeChildren: true,
    includeGuardians: true,
    includePetGuardians: true,
    includeBeneficiaries: true,
    includeExecutors: true,
  },
} as const;

// Type for person-like objects in the store
interface PersonLike {
  fullName?: string;
  relationship?: string;
  address?: Address;
  dateOfBirth?: string;
  isOver18?: boolean;
}

/**
 * Hook to get saved people from the will store based on configuration options.
 * Centralizes the logic for gathering people from different parts of the will data.
 */
export function useSavedPeople(options: UseSavedPeopleOptions = {}): SavedPerson[] {
  const { data } = useWillStore();
  const manuallyAddedPeople = data.manuallyAddedPeople || [];

  const savedPeople = useMemo(() => {
    const people: SavedPerson[] = [];
    const addedNames = new Set<string>();

    const addPerson = (fullName: string, relationship: string, address?: Address) => {
      if (fullName && !addedNames.has(fullName)) {
        people.push({ fullName, relationship, address });
        addedNames.add(fullName);
      }
    };

    const personalAddress = data.personalDetails?.address;
    const maritalStatus = data.personalDetails?.maritalStatus;

    // Spouse/Partner
    if (options.includeSpouse && data.personalDetails?.spouseFullName) {
      const rel = maritalStatus === 'civil_partnership' ? 'Partner' :
        maritalStatus === 'cohabiting' ? 'Partner' : 'Spouse';
      const address = data.personalDetails?.spouseLivesWithYou === false
        ? data.personalDetails?.spouseAddress
        : personalAddress;
      addPerson(data.personalDetails.spouseFullName, rel, address);
    }

    // Engaged Partner (Fiancé/Fiancée)
    const personalDetailsExt = data.personalDetails as (typeof data.personalDetails & {
      intendedPartnerFullName?: string;
      intendedPartnerLivesWithYou?: boolean;
      intendedPartnerAddress?: Address;
    });
    if (options.includeEngagedPartner && personalDetailsExt?.intendedPartnerFullName) {
      const address = personalDetailsExt?.intendedPartnerLivesWithYou === false
        ? personalDetailsExt?.intendedPartnerAddress
        : personalAddress;
      addPerson(personalDetailsExt.intendedPartnerFullName, 'Fiancé(e)', address);
    }

    // Children
    if (options.includeChildren && data.children && data.children.length > 0) {
      data.children.forEach((child) => {
        const childExt = child as PersonLike;
        if (childExt?.fullName) {
          // If adultsOnlyChildren is set, check age
          if (options.adultsOnlyChildren) {
            const isAgeVerified = isOver18(childExt.dateOfBirth);
            const explicitUnder18 = childExt.isOver18 === false;
            if (!isAgeVerified || explicitUnder18) {
              return; // Skip this child
            }
          }
          addPerson(childExt.fullName, 'Child', personalAddress);
        }
      });
    }

    // Guardians
    if (options.includeGuardians && data.guardians && data.guardians.length > 0) {
      data.guardians.forEach((guardian) => {
        const guardianExt = guardian as PersonLike;
        if (guardianExt?.fullName) {
          addPerson(guardianExt.fullName, guardianExt.relationship || 'Guardian', guardianExt.address);
        }
      });
    }

    // Pet Guardians
    if (options.includePetGuardians && data.petGuardians && data.petGuardians.length > 0) {
      data.petGuardians.forEach((petGuardian) => {
        const petGuardianExt = petGuardian as PersonLike;
        if (petGuardianExt?.fullName) {
          addPerson(petGuardianExt.fullName, 'Pet Guardian', petGuardianExt.address);
        }
      });
    }

    // Executors
    if (options.includeExecutors && data.executors && data.executors.length > 0) {
      data.executors.forEach((executor) => {
        const executorExt = executor as PersonLike;
        if (executorExt?.fullName && executorExt.relationship !== 'Professional') {
          addPerson(executorExt.fullName, executorExt.relationship || 'Executor', executorExt.address);
        }
      });
    }

    // Substitute Executors
    if (options.includeSubstituteExecutors && data.substituteExecutors && data.substituteExecutors.length > 0) {
      data.substituteExecutors.forEach((executor) => {
        const executorExt = executor as PersonLike;
        if (executorExt?.fullName && executorExt.relationship !== 'Professional') {
          addPerson(executorExt.fullName, executorExt.relationship || 'Substitute Executor', executorExt.address);
        }
      });
    }

    // Beneficiaries (excluding charities)
    if (options.includeBeneficiaries && data.beneficiaries && data.beneficiaries.length > 0) {
      data.beneficiaries.forEach((beneficiary) => {
        const beneficiaryExt = beneficiary as PersonLike;
        if (beneficiaryExt?.fullName && beneficiaryExt.relationship !== 'Charity') {
          addPerson(beneficiaryExt.fullName, beneficiaryExt.relationship || 'Beneficiary', beneficiaryExt.address);
        }
      });
    }

    // Manually Added People
    if (options.includeManuallyAdded && manuallyAddedPeople && manuallyAddedPeople.length > 0) {
      manuallyAddedPeople.forEach((person) => {
        if (person?.fullName) {
          addPerson(person.fullName, person.relationship || 'Person', person.address);
        }
      });
    }

    return people;
  }, [data, manuallyAddedPeople, options]);

  return savedPeople;
}
